import classNames from 'classnames';
import { useEffect, useState } from 'react';
import CheckboxGroup, {
  CheckboxOption,
} from '@/components/organisms/checkbox-group/checkbox-group';
import { LocationGroup } from '@/components/organisms/search-bar/search-bar.types';
import MasonryGrid from '@/components/utilities/masonry-grid/masonry-grid';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import styles from './search-bar-locations-checkbox-wrapper.module.scss';

export interface SearchBarLocationsCheckboxWrapperProps {
  /** The class name to apply */
  className?: string;
  /** The locations to be displayed */
  locations: LocationGroup[];
  /** Masonry grid settings */
  masonrySettings?: Content.Components['SearchSettingsDto']['masonrySettings'];
  /** An event that is triggered on change of locations */
  onUpdateLocations: (updatedLocations: LocationGroup[]) => void;
  /** The layout to use for the links */
  useMasonryLayout?: Content.Components['SearchSettingsDto']['useMasonryLayout'];
}

const SearchBarLocationsCheckboxWrapper = ({
  className,
  locations,
  masonrySettings,
  onUpdateLocations,
  useMasonryLayout,
}: SearchBarLocationsCheckboxWrapperProps) => {
  const [updatedLocations, setUpdatedLocations] =
    useState<LocationGroup[]>(locations);
  const isTablet = useWindowSize('tablet');
  const isLaptop = useWindowSize('laptop');
  const isDesktop = useWindowSize('desktop');

  const maxColumns = isDesktop ? 4 : isLaptop ? 3 : isTablet ? 2 : 1;

  const requestedMasonryColumns =
    masonrySettings?.columnNumber === 'fourColumns'
      ? 4
      : masonrySettings?.columnNumber === 'threeColumns'
        ? 3
        : undefined;

  const masonryColumns = Math.min(
    Math.max(requestedMasonryColumns ?? 1, 1),
    maxColumns,
  );

  useEffect(() => {
    setUpdatedLocations(locations);
  }, [locations]);

  const handleLocationChange = (
    groupName: string,
    updatedOptions: CheckboxOption[],
  ) => {
    const newLocations = updatedLocations.map(locationGroup => {
      if (locationGroup.groupName === groupName) {
        return {
          ...locationGroup,
          options: updatedOptions,
        };
      }

      return locationGroup;
    });

    setUpdatedLocations(newLocations);
    onUpdateLocations(newLocations);
  };

  const renderLocationGroup = (locationGroup: LocationGroup, index: number) => (
    <CheckboxGroup
      key={locationGroup.groupName || index}
      groupName={locationGroup.groupName}
      onChange={updatedOptions =>
        handleLocationChange(locationGroup.groupName, updatedOptions)
      }
      options={locationGroup.options}
    />
  );

  return (
    <div className={classNames(styles.component, className)}>
      {useMasonryLayout ? (
        <MasonryGrid
          balanced={masonrySettings?.useCompactLayout}
          className={styles.masonryGrid}
          columns={masonryColumns}
          fillDirection={masonrySettings?.fillDirection}
          gap={32}
          itemClassName={styles.item}
        >
          {updatedLocations.map(renderLocationGroup)}
        </MasonryGrid>
      ) : (
        <div className={styles.defaultGrid}>
          {updatedLocations.map(renderLocationGroup)}
        </div>
      )}
    </div>
  );
};

SearchBarLocationsCheckboxWrapper.displayName =
  'SearchBarLocationsCheckboxWrapper';

export default SearchBarLocationsCheckboxWrapper;
